import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api, API } from "../lib/api";
import {
  ArrowDownCircle, ArrowUpCircle, Calculator, Download,
  Loader2, BookOpen, FileText,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const TABS = [
  { v: "supported", icon: ArrowDownCircle },
  { v: "charged", icon: ArrowUpCircle },
  { v: "303", icon: Calculator },
  { v: "390", icon: FileText },
];

const QUARTERS = [
  { v: 0, l: { ca: "Any complet", es: "Año completo" } },
  { v: 1, l: { ca: "T1 (Gen-Mar)", es: "T1 (Ene-Mar)" } },
  { v: 2, l: { ca: "T2 (Abr-Jun)", es: "T2 (Abr-Jun)" } },
  { v: 3, l: { ca: "T3 (Jul-Set)", es: "T3 (Jul-Sep)" } },
  { v: 4, l: { ca: "T4 (Oct-Des)", es: "T4 (Oct-Dic)" } },
];

const RATE_COLS = [21, 10, 4, 0];

const fmt = (n) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(n) || 0);

export default function TaxBooks() {
  const { lang } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [tab, setTab] = useState("supported");
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(null);
  const [charged, setCharged] = useState(null);
  const [m303, setM303] = useState(null);
  const [m390, setM390] = useState(null);

  const params = useMemo(() => {
    const q = { year };
    if (quarter) q.quarter = quarter;
    return q;
  }, [year, quarter]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/tax-books/iva-supported", { params }),
        api.get("/tax-books/iva-charged", { params }),
        api.get("/tax-books/model-303", { params }),
        api.get("/tax-books/model-390", { params: { year } }),
      ]);
      setSupported(a.data);
      setCharged(b.data);
      setM303(c.data);
      setM390(d.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, quarter]); // eslint-disable-line

  const downloadCsv = (kind) => {
    const token = localStorage.getItem("hosys_token") || "";
    const p = new URLSearchParams({ year, token });
    if (quarter) p.append("quarter", quarter);
    const url = `${API}/tax-books/${kind}.csv?${p.toString()}`;
    window.open(url, "_blank");
  };

  const downloadErp = (erp, book) => {
    const token = localStorage.getItem("hosys_token") || "";
    const p = new URLSearchParams({ year, token, book });
    if (quarter) p.append("quarter", quarter);
    const url = `${API}/tax-books/export/${erp}.csv?${p.toString()}`;
    window.open(url, "_blank");
  };

  const downloadPdf303 = () => {
    const token = localStorage.getItem("hosys_token") || "";
    const p = new URLSearchParams({ year, token });
    if (quarter) p.append("quarter", quarter);
    const url = `${API}/tax-books/model-303.pdf?${p.toString()}`;
    window.open(url, "_blank");
  };

  const yearOptions = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 4; y--) {
    yearOptions.push(y);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-[0_0_18px_rgba(99,102,241,0.35)]">
              <BookOpen className="w-4.5 h-4.5 text-white" size={18} />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              {lang === "es" ? "Libros fiscales" : "Llibres fiscals"}
            </h1>
          </div>
          <p className="text-sm text-slate-400 mt-2 ml-12">
            {lang === "es"
              ? "IVA repercutido, IVA soportado y resumen del Modelo 303"
              : "IVA repercutit, IVA suportat i resum del Model 303"}
          </p>
        </div>
      </div>

      {/* Period selector */}
      <div className="card-surface p-4 flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-wider text-slate-400">
          {lang === "es" ? "Período" : "Període"}
        </label>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-400/50"
          data-testid="tb-year"
        >
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex flex-wrap items-center gap-1.5">
          {QUARTERS.map((q) => (
            <button
              key={q.v}
              onClick={() => setQuarter(q.v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                quarter === q.v
                  ? "bg-indigo-600 border-indigo-500 text-white"
                  : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
              }`}
              data-testid={`tb-quarter-${q.v}`}
            >
              {q.l[lang === "es" ? "es" : "ca"]}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5">
        {TABS.map((t) => {
          const active = tab === t.v;
          const label =
            t.v === "supported"
              ? (lang === "es" ? "IVA soportado" : "IVA suportat")
              : t.v === "charged"
              ? (lang === "es" ? "IVA repercutido" : "IVA repercutit")
              : t.v === "303"
              ? "Model 303"
              : "Model 390";
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all flex items-center gap-2 ${
                active
                  ? "bg-blue-600 border-blue-500 text-white shadow-[0_0_18px_rgba(59,130,246,0.3)]"
                  : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
              }`}
              data-testid={`tb-tab-${t.v}`}
            >
              <t.icon size={14} />
              <span>{label}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="card-surface p-12 text-center text-slate-400">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
          {lang === "es" ? "Cargando..." : "Carregant..."}
        </div>
      ) : (
        <>
          {tab === "supported" && (
            <LedgerView
              data={supported}
              kind="supported"
              lang={lang}
              onDownload={() => downloadCsv("iva-supported")}
              onErpExport={(erp) => downloadErp(erp, "received")}
              testid="tb-supported"
            />
          )}
          {tab === "charged" && (
            <LedgerView
              data={charged}
              kind="charged"
              lang={lang}
              onDownload={() => downloadCsv("iva-charged")}
              onErpExport={(erp) => downloadErp(erp, "issued")}
              testid="tb-charged"
            />
          )}
          {tab === "303" && <Model303View data={m303} lang={lang} onPdf={downloadPdf303} />}
          {tab === "390" && <Model390View data={m390} lang={lang} />}
        </>
      )}
    </div>
  );
}

function LedgerView({ data, kind, lang, onDownload, onErpExport, testid }) {
  if (!data) return null;
  const isCharged = kind === "charged";

  const totalRow = (rate) => {
    const r = data.totals.by_rate.find((x) => x.rate === rate);
    return r || { base: 0, vat: 0 };
  };

  const ERPS = [
    { v: "sage50", l: "Sage 50" },
    { v: "a3", l: "A3" },
    { v: "contaplus", l: "ContaPlus" },
  ];

  return (
    <div className="space-y-4" data-testid={testid}>
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label={lang === "es" ? "Facturas" : "Factures"} value={data.count} />
        <Kpi label={lang === "es" ? "Base imponible" : "Base imposable"} value={fmt(data.totals.base)} />
        <Kpi label={isCharged ? "IVA repercutit" : "IVA suportat"}
             value={fmt(data.totals.vat)} accent={isCharged ? "emerald" : "blue"} />
        <Kpi label="Total" value={fmt(data.totals.total)} accent="indigo" />
      </div>

      {/* ERP export bar */}
      <div className="card-surface p-3 flex flex-wrap items-center gap-2"
           data-testid={`${testid}-erp-bar`}>
        <span className="text-[11px] uppercase tracking-wider text-slate-400 mr-1">
          {lang === "es" ? "Exportar a ERP:" : "Exporta a ERP:"}
        </span>
        {ERPS.map((e) => (
          <Button
            key={e.v}
            variant="outline"
            size="sm"
            onClick={() => onErpExport?.(e.v)}
            disabled={!data.count}
            data-testid={`${testid}-erp-${e.v}`}
            className="border-indigo-500/30 bg-indigo-500/5 text-indigo-200 hover:bg-indigo-500/15 disabled:opacity-40"
          >
            <Download size={12} className="mr-1.5" />
            {e.l}
          </Button>
        ))}
        <span className="text-[10px] text-slate-500 ml-auto">
          {lang === "es"
            ? "Apuntes contables (doble partida)"
            : "Assentaments comptables (doble partida)"}
        </span>
      </div>

      {/* Per-rate breakdown */}
      <div className="card-surface p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">
            {lang === "es" ? "Desglose por tipo de IVA" : "Desglossament per tipus d'IVA"}
          </h3>
          <Button variant="outline" size="sm" onClick={onDownload}
                  data-testid={`${testid}-csv-btn`}
                  className="border-white/10 text-slate-300 hover:bg-white/5">
            <Download size={12} className="mr-1.5" />
            CSV
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {RATE_COLS.map((rate) => {
            const row = totalRow(rate);
            return (
              <div key={rate}
                   className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                  {rate}% {lang === "es" ? "IVA" : "IVA"}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {lang === "es" ? "Base" : "Base"}:{" "}
                  <span className="text-slate-200 font-mono">{fmt(row.base)}</span>
                </div>
                <div className="text-base text-white font-mono font-semibold mt-0.5">
                  {fmt(row.vat)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed table */}
      <div className="card-surface overflow-hidden">
        {data.rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">
            {lang === "es" ? "Sin facturas en este período" : "Sense factures en aquest període"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-slate-400 uppercase tracking-wider">
                  <th className="text-left p-3">{lang === "es" ? "Fecha" : "Data"}</th>
                  <th className="text-left p-3">Nº</th>
                  <th className="text-left p-3">{isCharged
                    ? (lang === "es" ? "Cliente" : "Client")
                    : (lang === "es" ? "Proveedor" : "Proveïdor")}</th>
                  <th className="text-left p-3">NIF/CIF</th>
                  <th className="text-right p-3">Base</th>
                  <th className="text-right p-3">IVA</th>
                  <th className="text-right p-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3 text-slate-300">{r.date || "—"}</td>
                    <td className="p-3 text-slate-300 font-mono">
                      {isCharged ? r.number : r.document_number}
                    </td>
                    <td className="p-3 text-slate-200">
                      {isCharged ? r.client_name : r.supplier_name}
                    </td>
                    <td className="p-3 text-slate-400 font-mono">
                      {isCharged ? r.client_nif : r.supplier_nif}
                    </td>
                    <td className="p-3 text-right text-slate-300 font-mono">{fmt(r.base)}</td>
                    <td className="p-3 text-right text-slate-300 font-mono">{fmt(r.vat_amount)}</td>
                    <td className="p-3 text-right text-white font-mono font-semibold">{fmt(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-white/[0.04] border-t border-white/10">
                <tr className="font-bold">
                  <td colSpan={4} className="p-3 text-slate-300 uppercase text-[10px] tracking-wider">
                    {lang === "es" ? "Total período" : "Total període"}
                  </td>
                  <td className="p-3 text-right text-white font-mono">{fmt(data.totals.base)}</td>
                  <td className="p-3 text-right text-white font-mono">{fmt(data.totals.vat)}</td>
                  <td className="p-3 text-right text-white font-mono">{fmt(data.totals.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Model303View({ data, lang, onPdf }) {
  if (!data) return null;
  const tone =
    data.result > 0 ? "rose"
    : data.result < 0 ? "emerald"
    : "slate";
  const toneCls = {
    rose: "from-rose-500/15 to-rose-600/5 border-rose-500/30 text-rose-200",
    emerald: "from-emerald-500/15 to-emerald-600/5 border-emerald-500/30 text-emerald-200",
    slate: "from-slate-500/10 to-slate-600/5 border-slate-500/30 text-slate-200",
  }[tone];
  const resultLabel = {
    to_pay: lang === "es" ? "A ingresar en Hacienda" : "A ingressar a Hisenda",
    to_compensate: lang === "es" ? "A compensar / devolver" : "A compensar / retornar",
    neutral: lang === "es" ? "Saldo neutro" : "Saldo neutre",
  }[data.result_label];

  return (
    <div className="space-y-4" data-testid="tb-303">
      {/* Hero */}
      <div className={`rounded-2xl border bg-gradient-to-br ${toneCls} p-6`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80 mb-2">
              {lang === "es" ? "Resultado Modelo 303" : "Resultat Model 303"}
              {data.quarter ? ` · T${data.quarter}` : ` · ${data.year}`}
            </div>
            <div className="text-4xl font-bold tabular-nums">{fmt(data.result)}</div>
            <div className="text-sm opacity-80 mt-1">{resultLabel}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-right text-xs opacity-80">
              <div>{data.issued_count} {lang === "es" ? "facturas emitidas" : "factures emeses"}</div>
              <div>{data.received_count} {lang === "es" ? "facturas recibidas" : "factures rebudes"}</div>
            </div>
            {onPdf && (
              <Button
                size="sm"
                onClick={onPdf}
                className="bg-white/15 hover:bg-white/25 text-white border-white/20"
                data-testid="tb-303-pdf-btn"
              >
                <Download size={12} className="mr-1.5" />
                PDF AEAT
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Side by side */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Block
          title={lang === "es" ? "IVA repercutido (ingresos)" : "IVA repercutit (ingressos)"}
          subtitle={lang === "es" ? "Lo que has cobrado a clientes" : "El que has cobrat als clients"}
          rows={data.charged}
          total={data.total_charged}
          accent="emerald"
        />
        <Block
          title={lang === "es" ? "IVA soportado (gastos)" : "IVA suportat (despeses)"}
          subtitle={lang === "es" ? "Lo que has pagado a proveedores" : "El que has pagat als proveïdors"}
          rows={data.supported}
          total={data.total_supported}
          accent="blue"
        />
      </div>

      {/* Final calc */}
      <div className="card-surface p-5">
        <div className="text-sm font-semibold text-white mb-3">
          {lang === "es" ? "Cálculo" : "Càlcul"}
        </div>
        <div className="space-y-1.5 font-mono text-sm">
          <Row label={lang === "es" ? "IVA repercutido" : "IVA repercutit"}
               value={fmt(data.total_charged)} sign="+" />
          <Row label={lang === "es" ? "IVA soportado" : "IVA suportat"}
               value={fmt(data.total_supported)} sign="−" />
          <div className="border-t border-white/10 pt-2 mt-2 flex items-center justify-between">
            <span className="text-slate-300 font-sans font-semibold">
              {lang === "es" ? "Resultado" : "Resultat"}
            </span>
            <span className={`font-bold text-lg ${
              data.result > 0 ? "text-rose-300"
              : data.result < 0 ? "text-emerald-300" : "text-slate-200"
            }`}>{fmt(data.result)}</span>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-4 leading-relaxed">
          {lang === "es"
            ? "Este resumen es orientativo. Para presentar el Modelo 303 oficial necesitas el formulario AEAT con datos identificativos completos. La generación del PDF oficial está prevista en la próxima fase."
            : "Aquest resum és orientatiu. Per presentar el Model 303 oficial cal el formulari AEAT amb dades identificatives completes. La generació del PDF oficial es preveu a la propera fase."}
        </p>
      </div>
    </div>
  );
}

function Model390View({ data, lang }) {
  if (!data) return null;
  const result = data.annual_result;
  const tone =
    result > 0 ? "rose" : result < 0 ? "emerald" : "slate";
  const toneCls = {
    rose: "from-rose-500/15 to-rose-600/5 border-rose-500/30 text-rose-200",
    emerald: "from-emerald-500/15 to-emerald-600/5 border-emerald-500/30 text-emerald-200",
    slate: "from-slate-500/10 to-slate-600/5 border-slate-500/30 text-slate-200",
  }[tone];

  return (
    <div className="space-y-4" data-testid="tb-390">
      {/* Annual hero */}
      <div className={`rounded-2xl border bg-gradient-to-br ${toneCls} p-6`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80 mb-2">
              {lang === "es" ? "Resumen anual · Modelo 390" : "Resum anual · Model 390"}
              {" · "}{data.year}
            </div>
            <div className="text-4xl font-bold tabular-nums">{fmt(result)}</div>
            <div className="text-sm opacity-80 mt-1">
              {result > 0
                ? (lang === "es" ? "Saldo anual a favor de Hacienda" : "Saldo anual a favor d'Hisenda")
                : result < 0
                ? (lang === "es" ? "Saldo anual a favor del contribuyente" : "Saldo anual a favor del contribuent")
                : (lang === "es" ? "Saldo anual neutro" : "Saldo anual neutre")}
            </div>
          </div>
          <div className="text-right text-xs opacity-80">
            <div>{data.issued_count} {lang === "es" ? "emitidas" : "emeses"} · {data.received_count} {lang === "es" ? "recibidas" : "rebudes"}</div>
            <div className="mt-1 text-[10px] opacity-60">
              ↑ {fmt(data.total_charged)} · ↓ {fmt(data.total_supported)}
            </div>
          </div>
        </div>
      </div>

      {/* Quarters grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.quarters.map((q) => {
          const tn =
            q.result > 0 ? "border-rose-500/30" :
            q.result < 0 ? "border-emerald-500/30" : "border-white/10";
          const sign =
            q.result > 0 ? "text-rose-300" :
            q.result < 0 ? "text-emerald-300" : "text-slate-200";
          return (
            <div key={q.quarter}
                 className={`card-surface border ${tn} p-4`}
                 data-testid={`tb-390-q${q.quarter}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                T{q.quarter}{" "}
                {q.quarter === 1 ? "(Gen-Mar)" :
                 q.quarter === 2 ? "(Abr-Jun)" :
                 q.quarter === 3 ? "(Jul-Set)" : "(Oct-Des)"}
              </div>
              <div className={`text-xl font-bold ${sign} tabular-nums`}>
                {fmt(q.result)}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {q.result_label === "to_pay"
                  ? (lang === "es" ? "A pagar" : "A pagar")
                  : q.result_label === "to_compensate"
                  ? (lang === "es" ? "A compensar" : "A compensar")
                  : (lang === "es" ? "Sin movimiento" : "Sense moviment")}
              </div>
              <div className="border-t border-white/5 mt-2 pt-2 space-y-0.5 text-[10px] text-slate-400">
                <div className="flex justify-between">
                  <span>{lang === "es" ? "Repercutido" : "Repercutit"}</span>
                  <span className="font-mono">{fmt(q.total_charged)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{lang === "es" ? "Soportado" : "Suportat"}</span>
                  <span className="font-mono">{fmt(q.total_supported)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-rate annual breakdown */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Block
          title={lang === "es" ? "IVA repercutido anual" : "IVA repercutit anual"}
          subtitle={lang === "es" ? "Desglose por tipo" : "Desglossament per tipus"}
          rows={data.annual_charged}
          total={data.total_charged}
          accent="emerald"
        />
        <Block
          title={lang === "es" ? "IVA soportado anual" : "IVA suportat anual"}
          subtitle={lang === "es" ? "Desglose por tipo" : "Desglossament per tipus"}
          rows={data.annual_supported}
          total={data.total_supported}
          accent="blue"
        />
      </div>

      {/* Movements summary */}
      <div className="card-surface p-5">
        <div className="text-sm font-semibold text-white mb-3">
          {lang === "es" ? "Movimientos anuales reales" : "Moviments anuals reals"}
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-rose-500/5 border border-rose-500/20 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              {lang === "es" ? "Pagado a Hacienda" : "Pagat a Hisenda"}
            </div>
            <div className="text-lg font-bold text-rose-300 tabular-nums">
              {fmt(data.total_paid_to_aeat)}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {lang === "es"
                ? "Suma de trimestres con saldo positivo"
                : "Suma de trimestres amb saldo positiu"}
            </div>
          </div>
          <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              {lang === "es" ? "Compensado / a devolver" : "Compensat / a retornar"}
            </div>
            <div className="text-lg font-bold text-emerald-300 tabular-nums">
              {fmt(data.total_compensated)}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {lang === "es"
                ? "Suma de trimestres con saldo negativo"
                : "Suma de trimestres amb saldo negatiu"}
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-4 leading-relaxed">
          {lang === "es"
            ? "El Modelo 390 oficial se presenta anualmente entre el 1 y el 30 de enero. Este resumen es orientativo — para la presentación oficial necesitas el formulario AEAT con desgloses adicionales (operaciones exentas, intracomunitarias, etc.)."
            : "El Model 390 oficial es presenta anualment entre l'1 i el 30 de gener. Aquest resum és orientatiu — per a la presentació oficial cal el formulari AEAT amb desglossaments addicionals (operacions exemptes, intracomunitàries, etc.)."}
        </p>
      </div>
    </div>
  );
}

function Block({ title, subtitle, rows, total, accent }) {
  const acc = {
    emerald: "border-emerald-500/20",
    blue: "border-blue-500/20",
  }[accent];
  return (
    <div className={`card-surface ${acc} p-4`}>
      <div className="text-sm font-semibold text-white">{title}</div>
      <div className="text-[11px] text-slate-500 mb-3">{subtitle}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center">
          —
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.rate}
                 className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-white/[0.02] border border-white/5">
              <span className="text-slate-400 w-12 font-mono">{r.rate}%</span>
              <span className="text-slate-300 font-mono flex-1 text-right">
                {fmt(r.base)}
              </span>
              <span className="text-white font-mono w-28 text-right">
                {fmt(r.vat)}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-white/5 mt-3 pt-2.5 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Total</span>
        <span className="text-white font-mono font-bold">{fmt(total)}</span>
      </div>
    </div>
  );
}

function Row({ label, value, sign }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-slate-300 font-sans flex items-center gap-2">
        <span className="text-slate-500 w-3">{sign}</span>
        {label}
      </span>
      <span className="text-slate-100">{value}</span>
    </div>
  );
}

function Kpi({ label, value, accent = "slate" }) {
  const colors = {
    slate: "from-slate-500/5 to-slate-600/5 border-white/10 text-slate-200",
    blue: "from-blue-500/10 to-blue-600/5 border-blue-500/20 text-blue-200",
    emerald: "from-emerald-500/10 to-emerald-600/5 border-emerald-500/20 text-emerald-200",
    indigo: "from-indigo-500/10 to-indigo-600/5 border-indigo-500/20 text-indigo-200",
  }[accent];
  return (
    <div className={`card-surface bg-gradient-to-br ${colors} border p-3.5`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      <div className="text-lg font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}
